import type { AssetKey } from "../assets/AssetManifest";
import type { AssetMap } from "../assets/loader";

export type ObstacleKind =
  | "grease_drum"
  | "oil_slick"
  | "mayo_puddle"
  | "kebab_spikes"
  | "deep_fryer"
  | "health_inspector"
  | "scooter"
  | "soda_can"
  | "brick_block"
  | "horizontal_dick"
  | "pipe"
  | "policeman"
  | "giant_dick"
  | "pastor"
  | "shit_pile"
  | "gap";

export type ObstacleDef = {
  kind: ObstacleKind;
  asset?: AssetKey;
  w: number;
  h: number;
  duckable?: boolean;
  groundHazard?: boolean;
  behavior:
    | "static"
    | "roll"
    | "hop"
    | "fly"
    | "float"
    | "bounce"
    | "charge"
    | "gap";
  relSpeed?: number;
};

export const OBSTACLE_DEFS: Record<Exclude<ObstacleKind, "gap">, ObstacleDef> =
  {
    grease_drum: {
      kind: "grease_drum",
      asset: "obs.grease_drum",
      w: 52,
      h: 52,
      behavior: "roll",
      relSpeed: -40,
    },
    oil_slick: {
      kind: "oil_slick",
      asset: "obs.oil_slick",
      w: 70,
      h: 24,
      groundHazard: true,
      behavior: "static",
    },
    mayo_puddle: {
      kind: "mayo_puddle",
      asset: "obs.mayo_puddle",
      w: 62,
      h: 24,
      groundHazard: true,
      behavior: "static",
    },
    kebab_spikes: {
      kind: "kebab_spikes",
      asset: "obs.kebab_spikes",
      w: 42,
      h: 48,
      behavior: "static",
    },
    deep_fryer: {
      kind: "deep_fryer",
      asset: "obs.deep_fryer",
      w: 78,
      h: 36,
      groundHazard: true,
      behavior: "static",
    },
    health_inspector: {
      kind: "health_inspector",
      asset: "enemy.health_inspector",
      w: 54,
      h: 72,
      duckable: true,
      behavior: "float",
    },
    scooter: {
      kind: "scooter",
      asset: "enemy.scooter",
      w: 82,
      h: 56,
      behavior: "charge",
      relSpeed: -70,
    },
    soda_can: {
      kind: "soda_can",
      asset: "enemy.soda_can",
      w: 30,
      h: 42,
      behavior: "bounce",
    },
    brick_block: {
      kind: "brick_block",
      asset: "obs.brick_block",
      w: 44,
      h: 44,
      behavior: "static",
    },
    horizontal_dick: {
      kind: "horizontal_dick",
      asset: "obs.horizontal_dick",
      w: 80,
      h: 42,
      behavior: "static",
    },
    pipe: {
      kind: "pipe",
      asset: "obs.pipe",
      w: 52,
      h: 72,
      behavior: "static",
    },
    policeman: {
      kind: "policeman",
      asset: "enemy.policeman",
      w: 60,
      h: 86,
      behavior: "charge",
      relSpeed: -55,
    },
    giant_dick: {
      kind: "giant_dick",
      asset: "enemy.giant_dick",
      w: 54,
      h: 90,
      behavior: "hop",
      relSpeed: -20,
    },
    pastor: {
      kind: "pastor",
      asset: "enemy.pastor",
      w: 66,
      h: 80,
      duckable: true,
      behavior: "float",
      relSpeed: -25,
    },
    shit_pile: {
      kind: "shit_pile",
      asset: "obs.shit_pile",
      w: 56,
      h: 38,
      behavior: "static",
    },
  };

export class Obstacle {
  active = false;
  kind: ObstacleKind = "kebab_spikes";
  x = 0;
  y = 0;
  w = 40;
  h = 40;
  vx = 0;
  vy = 0;
  rot = 0;
  phase = 0;
  duckable = false;
  groundHazard = false;
  behavior: ObstacleDef["behavior"] = "static";
  asset?: AssetKey;
  gapWidth = 0;
  padX = 4;
  padY = 4;

  spawn(
    kind: ObstacleKind,
    x: number,
    groundY: number,
    _scrollSpeed: number,
    gapWidth = 0
  ): void {
    this.active = true;
    this.kind = kind;
    this.x = x;
    this.phase = Math.random() * Math.PI * 2;
    this.rot = 0;
    this.vx = 0;
    this.vy = 0;
    this.gapWidth = 0;

    if (kind === "gap") {
      this.behavior = "gap";
      this.gapWidth = gapWidth || 130;
      this.w = this.gapWidth;
      this.h = 20;
      this.y = groundY;
      this.duckable = false;
      this.groundHazard = false;
      this.asset = undefined;
      return;
    }

    const def = OBSTACLE_DEFS[kind];
    this.w = def.w;
    this.h = def.h;
    this.behavior = def.behavior;
    this.duckable = !!def.duckable;
    this.groundHazard = !!def.groundHazard;
    this.asset = def.asset;
    this.vx = def.relSpeed ?? 0;

    switch (def.behavior) {
      case "fly":
        this.y = groundY - 55;
        break;
      case "float":
        this.y = groundY - 58;
        break;
      case "bounce":
        this.y = groundY;
        this.vy = -380;
        break;
      default:
        this.y = groundY;
    }
  }

  update(dt: number, groundY: number): void {
    if (!this.active) return;
    this.phase += dt * 5;
    this.x += this.vx * dt;

    switch (this.behavior) {
      case "roll":
        this.rot += dt * 6;
        this.y = groundY;
        break;
      case "hop":
        this.y = groundY - Math.abs(Math.sin(this.phase)) * 22;
        break;
      case "fly":
        this.y = groundY - 55 + Math.sin(this.phase) * 6;
        break;
      case "float":
        this.y = groundY - 58 + Math.sin(this.phase * 0.8) * 8;
        break;
      case "bounce": {
        this.vy += 1600 * dt;
        this.y += this.vy * dt;
        if (this.y >= groundY) {
          this.y = groundY;
          this.vy = -400;
        }
        break;
      }
      case "charge":
      case "gap":
      case "static":
        if (this.behavior !== "gap") this.y = groundY;
        break;
    }
  }

  offscreen(camX: number): boolean {
    const right =
      this.kind === "gap" ? this.x + this.gapWidth : this.x + this.w / 2;
    return right < camX - 80;
  }

  get hitbox(): { x: number; y: number; w: number; h: number } {
    if (this.behavior === "gap") {
      return { x: this.x, y: this.y - 10, w: this.gapWidth, h: 40 };
    }

    let padX = this.padX;
    let padY = this.padY;
    if (this.groundHazard) {
      padX = 6;
      padY = 2;
    }
    if (this.duckable) {
      padX = 6;
      padY = 6;
    }

    // Extend hitbox width for fast-moving obstacles to prevent passthrough
    let extraW = 0;
    if (this.vx !== 0) {
      extraW = Math.abs(this.vx) * (1 / 60) * 2;
    }

    return {
      x: this.x - this.w / 2 + padX - extraW / 2,
      y: this.y - this.h + padY,
      w: this.w - padX * 2 + extraW,
      h: this.h - padY * 2,
    };
  }

  isJumpClearable(): boolean {
    if (this.kind === "gap") return true;
    if (this.duckable) return true;
    return this.h <= 64 || !!this.groundHazard;
  }

  draw(ctx: CanvasRenderingContext2D, camX: number, assets: AssetMap): void {
    if (!this.active || this.kind === "gap") return;
    const img = this.asset ? assets[this.asset] : null;
    if (!img) return;
    const sx = this.x - camX;
    const sy = this.y;
    ctx.save();
    ctx.translate(sx, sy);
    if (this.behavior === "roll") {
      ctx.rotate(this.rot);
      ctx.drawImage(img, -this.w / 2, -this.h / 2, this.w, this.h);
    } else {
      ctx.drawImage(img, -this.w / 2, -this.h, this.w, this.h);
    }
    ctx.restore();
  }
}

export function aabb(
  a: { x: number; y: number; w: number; h: number },
  b: { x: number; y: number; w: number; h: number }
): boolean {
  return (
    a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y
  );
}
